import { AIModule } from '@/data/ai/types';

export const DATA_PIPELINE_MONITORING_MODULE: AIModule = {
  id: 'data-pipeline-monitoring',
  title: 'Pipeline Monitoring & Observability',
  description: 'Implement comprehensive monitoring, alerting, and observability for ML pipelines.',
  duration: '2 hours',
  difficulty: 'intermediate',
  order: 3,
  content: `
## What You'll Learn
- Set up monitoring for data pipelines and ML models
- Implement data quality metrics and alerts
- Detect and handle model drift
- Build observability dashboards

## Overview
Monitoring ensures your ML systems remain healthy in production. It covers data quality, model performance, and infrastructure health.

---

## Key Concepts

### 1. Data Quality Monitoring
Track data quality metrics continuously:

\`\`\`python
from dataclasses import dataclass
from typing import Dict, Any

@dataclass
class DataQualityMetrics:
    null_percentage: float
    row_count: int
    schema_matches: bool
    freshness_hours: float

def check_data_quality(df) -> DataQualityMetrics:
    return DataQualityMetrics(
        null_percentage=df.isnull().mean().mean() * 100,
        row_count=len(df),
        schema_matches=validate_schema(df),
        freshness_hours=get_data_age(df)
    )
\`\`\`

### 2. Model Drift Detection
Monitor for prediction drift:

\`\`\`python
from scipy import stats

def detect_drift(reference, current, threshold=0.05):
    """Detect drift using KS test"""
    statistic, p_value = stats.ks_2samp(reference, current)
    
    return {
        'drift_detected': p_value < threshold,
        'p_value': p_value,
        'statistic': statistic
    }

# Alert if drift detected
if detect_drift(train_preds, prod_preds)['drift_detected']:
    send_alert("Model drift detected!")
\`\`\`

---

## Best Practices
- **Multi-level Alerts**: Info, Warning, Critical levels
- **Dashboards**: Visualize trends over time
- **Anomaly Detection**: Automated drift detection
- **SLOs/SLIs**: Define service level objectives

---

## Key Takeaways
✅ Monitor data quality at ingestion
✅ Track model performance continuously
✅ Detect drift before it impacts users
✅ Use dashboards for visibility

## Resources
- [MLflow Tracking](https://mlflow.org/docs/latest/tracking.html)
- [Great Expectations](https://greatexpectations.io/)
`,
  learningObjectives: [
    'Implement data quality monitoring',
    'Detect model drift',
    'Set up alerting systems',
    'Build observability dashboards'
  ],
  quiz: {
    questions: [
      {
        id: 'dpm-q1',
        question: 'What is model drift?',
        options: ['Model moving to new server', 'Change in model performance over time', 'Model file corruption', 'Version change'],
        correctAnswer: 1,
        explanation: 'Model drift refers to degradation in model performance due to changes in data distribution over time.'
      },
      {
        id: 'dpm-q2',
        question: 'Which test is commonly used for drift detection?',
        options: ['T-test', 'Chi-square test', 'Kolmogorov-Smirnov test', 'ANOVA'],
        correctAnswer: 2,
        explanation: 'The Kolmogorov-Smirnov (KS) test compares distributions and is commonly used for drift detection.'
      },
      {
        id: 'dpm-q3',
        question: 'What does SLO stand for?',
        options: ['System Load Optimizer', 'Service Level Objective', 'Supervised Learning Output', 'Standard Logging Operation'],
        correctAnswer: 1,
        explanation: 'SLO stands for Service Level Objective - a target value for service reliability.'
      },
      {
        id: 'dpm-q4',
        question: 'Which metric measures data freshness?',
        options: ['Null percentage', 'Row count', 'Data age/latency', 'Schema validation'],
        correctAnswer: 2,
        explanation: 'Data age or latency measures how fresh/recent the data is.'
      },
      {
        id: 'dpm-q5',
        question: 'What is Great Expectations used for?',
        options: ['Model training', 'Data validation', 'Feature engineering', 'Hyperparameter tuning'],
        correctAnswer: 1,
        explanation: 'Great Expectations is a Python library for data validation and documentation.'
      }
    ]
  },
  practice: {
    task: 'Build a Data Quality Monitor: Create a monitoring class that checks data quality and sends alerts. Implement a DataMonitor class with quality checks and alerting.',
    starter: `import pandas as pd
from datetime import datetime

class DataMonitor:
    def __init__(self, thresholds):
        self.thresholds = thresholds
    
    def check_null_rate(self, df):
        # TODO: Calculate null percentage
        pass
    
    def check_row_count(self, df):
        # TODO: Check minimum row count
        pass
    
    def run_all_checks(self, df):
        # TODO: Run all checks and return results
        pass
    
    def send_alert(self, message):
        # TODO: Print alert (in real system, send email/Slack)
        pass`,
    solution: `import pandas as pd
from datetime import datetime

class DataMonitor:
    def __init__(self, thresholds):
        self.thresholds = thresholds
        self.alerts = []
    
    def check_null_rate(self, df):
        null_rate = df.isnull().mean().mean() * 100
        passed = null_rate <= self.thresholds.get('max_null_pct', 5)
        return {'metric': 'null_rate', 'value': null_rate, 'passed': passed}
    
    def check_row_count(self, df):
        row_count = len(df)
        min_rows = self.thresholds.get('min_rows', 100)
        passed = row_count >= min_rows
        return {'metric': 'row_count', 'value': row_count, 'passed': passed}
    
    def run_all_checks(self, df):
        results = [
            self.check_null_rate(df),
            self.check_row_count(df)
        ]
        
        for result in results:
            if not result['passed']:
                self.send_alert(f"FAILED: {result['metric']} = {result['value']}")
        
        return {
            'timestamp': datetime.now().isoformat(),
            'all_passed': all(r['passed'] for r in results),
            'checks': results
        }
    
    def send_alert(self, message):
        alert = f"[ALERT] {datetime.now()}: {message}"
        self.alerts.append(alert)
        print(alert)

# Test it
monitor = DataMonitor({'max_null_pct': 5, 'min_rows': 100})
df = pd.DataFrame({'a': [1, 2, None], 'b': [4, 5, 6]})
results = monitor.run_all_checks(df)
print(results)`,
    hints: [
      'Use df.isnull().mean() for null rates',
      'Store alerts in a list for tracking',
      'Return structured results for dashboards'
    ],
    estimatedTime: 25
  }
};
